import {faCircleExclamation} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Input, Select, Tooltip} from 'antd';
import {ChangeEvent} from 'react';

import {useDomainConfig} from '../../../../../../../../provider/domain-config/domain-config-hook';
import {Locale} from '../../../../../../../../provider/locale/locale';
import {ProtocolEnum} from '../../../../../../../../service/company-v2/company-const';
import {useRegularSocialsApi} from '../../../../../../../../service/company-v2/company-hook';
import {
    CompanyFormSocialNetworkType,
    CompanyFormWebsiteType,
} from '../../../../../../../../service/company-v2/types/company-links-type';
import {Form} from '../../../../../../../../typings/antd';
import {classNames} from '../../../../../../../../util/css';
import {ProvidersIdsEnum} from '../../../../../../../../util/type';
import {extractProtocol, MessengerRegexpEntryType} from '../../../../company-form-helper';

import {getProtocolOptions} from './url-input-helper';
import * as styles from './url-input.scss';

type PropsType = {
    value?: CompanyFormWebsiteType | CompanyFormSocialNetworkType;
    onChange?: (newValue: Partial<CompanyFormWebsiteType | CompanyFormSocialNetworkType>) => void;
    size: 'middle' | 'large';
    placeholder: string;
    httpsOnly?: boolean;
    catalogId?: number;
};

const WHATSAPP_PLACEHOLDER = 'wa.me/1234567890';
const VIBER_PLACEHOLDER = 'viber.click/1234567890';
const MAX_PLACEHOLDER = 'max.ru/id1234567890';

const MESSENGER_NAME_TO_PROTOCOL: Record<string, ProtocolEnum> = {
    whatsapp: ProtocolEnum.WHATSAPP,
    viber: ProtocolEnum.VIBER,
    max: ProtocolEnum.MAX,
};

export function UrlInput(props: PropsType): JSX.Element {
    const {value, onChange, size, placeholder, httpsOnly, catalogId} = props;
    const {status} = Form.Item.useStatus();
    const {isShowMaxSocialNetworkPrefix} = useDomainConfig();
    const {data: socialsData} = useRegularSocialsApi();

    const showMessengerOptions = catalogId === ProvidersIdsEnum.doubleGis && Boolean(httpsOnly);
    const showMaxOption = isShowMaxSocialNetworkPrefix && Boolean(httpsOnly);
    const isSelectDisabled = Boolean(httpsOnly) && !showMessengerOptions && !showMaxOption;

    const messengerRegexps: Array<MessengerRegexpEntryType> =
        showMessengerOptions || showMaxOption
            ? (socialsData ?? []).reduce<Array<MessengerRegexpEntryType>>((accumulator, item) => {
                  const name = item.name.toLowerCase();
                  const protocol = MESSENGER_NAME_TO_PROTOCOL[name];

                  if (!protocol) {
                      return accumulator;
                  }

                  if (protocol === ProtocolEnum.MAX && !showMaxOption) {
                      return accumulator;
                  }

                  if (
                      (protocol === ProtocolEnum.WHATSAPP || protocol === ProtocolEnum.VIBER) &&
                      !showMessengerOptions
                  ) {
                      return accumulator;
                  }

                  accumulator.push({regexp: item.regexp, protocol});

                  return accumulator;
              }, [])
            : [];

    function handleValueChange(event: ChangeEvent<{value: string}>) {
        const inputValue = event.target.value;
        const protocolData = extractProtocol(inputValue, {messengerRegexps});

        if (!protocolData) {
            onChange?.({...value, value: inputValue});

            return;
        }

        const protocol =
            isSelectDisabled && protocolData.protocol !== ProtocolEnum.MAX ? value?.protocol : protocolData.protocol;

        // MAX: only switch prefix; keep the raw input value (normalize on save).
        const nextValue = protocolData.protocol === ProtocolEnum.MAX ? inputValue : protocolData.value;

        onChange?.({...value, value: nextValue, protocol});
    }

    function handleProtocolChange(protocol: ProtocolEnum) {
        if (isSelectDisabled) {
            return;
        }

        onChange?.({...(value || {value: ''}), protocol});
    }

    function getResolvedPlaceholder(): string {
        if (value?.protocol === ProtocolEnum.WHATSAPP) {
            return WHATSAPP_PLACEHOLDER;
        }

        if (value?.protocol === ProtocolEnum.VIBER) {
            return VIBER_PLACEHOLDER;
        }

        if (value?.protocol === ProtocolEnum.MAX) {
            return MAX_PLACEHOLDER;
        }

        return placeholder;
    }

    const resolvedPlaceholder = getResolvedPlaceholder();

    return (
        <Input
            addonBefore={
                isSelectDisabled ? (
                    <Select<ProtocolEnum>
                        className={classNames({[styles.url_input__select_disabled]: isSelectDisabled})}
                        onChange={handleProtocolChange}
                        options={getProtocolOptions(Boolean(httpsOnly), showMessengerOptions, showMaxOption)}
                        size={size}
                        suffixIcon={null}
                        value={value?.protocol}
                    />
                ) : (
                    <Select<ProtocolEnum>
                        className={classNames({[styles.url_input__select_disabled]: isSelectDisabled})}
                        onChange={handleProtocolChange}
                        options={getProtocolOptions(Boolean(httpsOnly), showMessengerOptions, showMaxOption)}
                        size={size}
                        value={value?.protocol}
                    />
                )
            }
            className={classNames({[styles.warningSelect]: status === 'warning'})}
            onChange={handleValueChange}
            placeholder={resolvedPlaceholder}
            size={size}
            suffix={
                <div>
                    {status === 'warning' && (
                        <Tooltip
                            placement="bottom"
                            title={<Locale stringKey="COMPANY_FORM__LINKS__SOCIAL_NETWORKS__TOOLTIP__WARNING" />}
                        >
                            <FontAwesomeIcon className={styles.icon} icon={faCircleExclamation} />
                        </Tooltip>
                    )}
                </div>
            }
            value={value?.value}
        />
    );
}
